import { DateTime } from 'luxon';

import { getDayKey } from '../scheduleHelpers';
import { getAllDayValues } from '../../helpers/time';

const LANE_LIMIT = 20;
export const BAR_HEIGHT = 20;
export const BAR_GAP = 4;
export const MORE_SLOT = 20;
export const BAR_TOP = 6;
const ROW_MIN = 60;
const ROW_MAX = 120;

const barBottom = (lane) => BAR_TOP + (lane * (BAR_HEIGHT + BAR_GAP)) + BAR_HEIGHT;

export const getDaysInRange = (startDate, numDays) => (
  new Array(numDays).fill(1).map((_, idx) => startDate.plus({ days: idx }))
);

/**
 * True when `shift` belongs on the row for `rowId`.
 * The unassigned row (no rowId) holds shifts with nothing in `shiftField`.
 */
export const isShiftInRow = (shift, rowId, shiftField) => {
  const shiftRowIds = shift?.[shiftField] || [];
  return rowId
    ? shiftRowIds.includes(rowId)
    : shiftRowIds.length === 0;
};

const coveredDayIndexes = (days, shift) => {
  const timezone = shift.timezone || DateTime.local().zoneName;
  const startInZone = DateTime.fromMillis(shift.startTime, { zone: timezone });
  const endInZone = DateTime.fromMillis(shift.endTime, { zone: timezone });
  const { isAllDay } = getAllDayValues(startInZone, endInZone);
  const startDT = isAllDay ? startInZone : DateTime.fromMillis(shift.startTime);
  const endDT = isAllDay ? endInZone : DateTime.fromMillis(shift.endTime);
  // An end time of exactly midnight belongs to the previous day.
  const inclusiveEnd = endDT.equals(endDT.startOf('day'))
    ? endDT.minus({ day: 1 }).endOf('day')
    : endDT;
  const covered = [];
  days.forEach((dt, idx) => {
    if (startDT <= dt.endOf('day') && inclusiveEnd >= dt.startOf('day')) covered.push(idx);
  });
  return covered;
};

export const layoutResourceRow = (row, shifts, days, shiftField) => {
  const byId = new Map();
  days.forEach((dt) => {
    const allShiftsForDay = shifts[getDayKey(dt)] ?? [];
    allShiftsForDay.forEach((shift) => {
      if (!isShiftInRow(shift, row?.id, shiftField) || byId.has(shift.id)) return;
      byId.set(shift.id, shift);
    });
  });

  const entries = [];
  byId.forEach((shift) => {
    const covered = coveredDayIndexes(days, shift);
    if (!covered.length) return;
    entries.push({
      shift,
      startIdx: covered[0],
      span: covered[covered.length - 1] - covered[0] + 1,
      covered,
    });
  });

  const lanesByDay = {};
  const assignLane = (entry) => {
    let lane = 0;
    while (lane < LANE_LIMIT) {
      const free = entry.covered.every((idx) => !lanesByDay[idx] || !lanesByDay[idx].has(lane));
      if (free) {
        entry.covered.forEach((idx) => {
          if (!lanesByDay[idx]) lanesByDay[idx] = new Set();
          lanesByDay[idx].add(lane);
        });
        entry.lane = lane;
        return;
      }
      lane += 1;
    }
    entry.lane = 0;
  };

  entries
    .filter((entry) => entry.span > 1)
    .sort((a, b) => b.span - a.span || a.shift.startTime - b.shift.startTime)
    .forEach(assignLane);
  entries
    .filter((entry) => entry.span === 1)
    .sort((a, b) => a.shift.startTime - b.shift.startTime)
    .forEach(assignLane);

  let visibleMaxLane = entries.length
    ? entries.reduce((max, entry) => Math.max(max, entry.lane), 0)
    : -1;
  while (visibleMaxLane >= 0) {
    const hidden = entries.some((entry) => entry.lane > visibleMaxLane);
    const limit = ROW_MAX - (hidden ? MORE_SLOT : 0);
    if (barBottom(visibleMaxLane) <= limit) break;
    visibleMaxLane -= 1;
  }
  entries.forEach((entry) => {
    entry.visible = entry.lane <= visibleMaxLane;
  });

  const hasHidden = entries.some((entry) => !entry.visible);
  const contentBottom = visibleMaxLane >= 0
    ? barBottom(visibleMaxLane) + BAR_TOP
    : 0;
  const rowHeight = hasHidden
    ? ROW_MAX
    : Math.min(ROW_MAX, Math.max(ROW_MIN, contentBottom));

  return { entries, visibleMaxLane, rowHeight };
};

export const getResourceRowLayout = ({
  row,
  shifts,
  startDate,
  numDays,
  shiftField,
}) => layoutResourceRow(row, shifts, getDaysInRange(startDate, numDays), shiftField);
